/**
 * Catchball Tier Turn Types
 *
 * A Tier Turn (renamed from Level Turn per the glossary level-vs-tier ruling; the schema keeps `catchball_level_turns` / `level_turn_id`) represents one active ball possession period at a specific
 * org unit within a Catchball loop.
 *
 * The ball moves one level at a time — down (request) or up (proposal/approval).
 * Each arrival at a level creates a new turn. The turn holder must complete
 * their judgment work before they can pass the ball.
 *
 * Passing up is the approval signal. There is no separate ratify step.
 *
 * Connection to Decision Rights
 * ─────────────────────────────
 * The ball holder at a level is the effective decision right-holder for
 * judgment decisions at that level within the loop. The involvement_mode
 * on a CatchballTierInvolvement maps directly to DecisionExecutionMode:
 *   - 'session'  → the right-holder exercises judgment via a structured session
 *   - 'delegate' → the right-holder delegates to the level below
 *
 * Schema: supabase/migrations/224_catchball_level_turns.sql
 */

// ============================================================================
// Core State Types
// ============================================================================

/**
 * How the ball arrived at this level.
 * - initiate: this level started the loop (top level opening a request)
 * - down:     ball was thrown down from the level above (request arriving)
 * - up:       ball was thrown up from the level below (proposal arriving)
 */
export type BallArrivalDirection = 'initiate' | 'down' | 'up';

/**
 * State of a level turn.
 * - active:        ball holder is doing judgment work
 * - ready_to_pass: all required judgment prerequisites satisfied; pass unlocked
 * - passed:        ball has been passed to the next level; turn is complete
 * - returned:      ball was sent back from above (turn ended by return)
 */
export type TierTurnState = 'active' | 'ready_to_pass' | 'passed' | 'returned';

/**
 * How a level is involved in another level's judgment work.
 * Maps directly to DecisionExecutionMode from decisionRights.ts.
 * - session:  target participates in the ball holder's judgment sessions
 * - delegate: ball holder sends a formal sub-request; target delivers a specific output
 */
export type InvolvementMode = 'session' | 'delegate';

/**
 * State of a level involvement.
 */
export type InvolvementState = 'open' | 'delivered' | 'cancelled';

// ============================================================================
// Judgment Prerequisites
// ============================================================================

/**
 * A single judgment prerequisite that must be satisfied before the ball
 * can be passed. Sourced from workflow config at turn creation time.
 */
export interface JudgmentPrerequisite {
  code: string;
  label: string;
  required: boolean;
  /** Optional description of what satisfies this prerequisite */
  description?: string;
}

/**
 * The satisfaction record for a single prerequisite.
 */
export interface PrerequisiteSatisfaction {
  satisfied: boolean;
  satisfiedAt?: string; // ISO 8601
  satisfiedBy?: string; // user id
  /** Reference to the artifact that satisfied this prerequisite */
  artifactRef?: {
    entityId: string;
    entityTable: string;
  };
}

/**
 * Map of prerequisite code → satisfaction state.
 */
export type PrerequisiteSatisfactionMap = Record<string, PrerequisiteSatisfaction>;

// ============================================================================
// Catchball Level Turn
// ============================================================================

/**
 * One active ball possession period at a specific org unit within a loop.
 *
 * Each time the ball arrives at a level (whether going down or up) a new
 * turn is created. L2 may therefore have two turns in a single loop pass:
 * one going down and one going up.
 */
export interface CatchballTierTurn {
  id: string;
  loopId: string;
  workspaceId?: string;

  /**
   * Branch ball this turn belongs to (catchball_balls.id). Null for legacy
   * single-ball loops that predate migration 609.
   */
  ballId?: string | null;

  /** The org unit that holds the ball during this turn */
  orgUnitId: string;

  /** How the ball arrived */
  ballDirection: BallArrivalDirection;

  /** Current state of this turn */
  state: TierTurnState;

  /** Where the ball came from (null for initiating turns) */
  receivedFromOrgUnitId?: string;
  receivedAt: string; // ISO 8601

  /** Payload received at this turn (request going down; proposal coming up) */
  receivedContent?: Record<string, unknown>;

  /** Judgment prerequisites for this turn (snapshot from workflow config) */
  judgmentPrerequisites: JudgmentPrerequisite[];

  /** Satisfaction state for each prerequisite */
  prerequisiteSatisfaction: PrerequisiteSatisfactionMap;

  /** When the turn was completed */
  passedAt?: string; // ISO 8601
  passedToOrgUnitId?: string;
  passedBy?: string;

  /**
   * Payload passed at the end of this turn.
   * Going down: the request being delegated to the level below.
   * Going up:   the proposal being submitted for approval.
   */
  passedContent?: Record<string, unknown>;

  /** Position in the loop's turn sequence (1 = first) */
  sequenceNumber: number;

  createdAt: string;
  createdBy?: string;
}

// ============================================================================
// Catchball Level Involvement
// ============================================================================

/**
 * An explicit involvement of one level in another's judgment work.
 *
 * The ball holder creates this record to grant visibility and engage the
 * level below. Without an involvement record, the target level has no
 * visibility into the loop request.
 *
 * - session mode:  target level participates in holder's sessions
 * - delegate mode: holder issues a sub-request; target delivers output
 */
export interface CatchballTierInvolvement {
  id: string;
  loopId: string;
  tierTurnId: string;
  workspaceId?: string;

  /** The ball holder org unit that is doing the involving */
  holderOrgUnitId: string;

  /** The org unit being involved */
  targetOrgUnitId: string;

  /** How the level is being involved (maps to DecisionExecutionMode) */
  involvementMode: InvolvementMode;

  /** For delegate mode: what specific output is being requested */
  requestContent?: Record<string, unknown>;

  state: InvolvementState;

  /** For delegate mode: when the output was delivered */
  deliveredAt?: string;
  deliveredContent?: Record<string, unknown>;
  deliveredBy?: string;

  createdAt: string;
  createdBy?: string;
}

// ============================================================================
// Derived / Computed Types
// ============================================================================

/**
 * The active ball position for a loop: which org unit currently holds the ball.
 * Prefer reading from catchball_active_branch_positions (getActivePositions)
 * over catchball_loops.ball_holder_scope, which is a display-only ternary.
 */
export interface ActiveBallPosition {
  loopId: string;
  turnId: string;
  ballHolderOrgUnitId: string;
  ballDirection: BallArrivalDirection;
  state: TierTurnState;
  receivedAt: string;
  sequenceNumber: number;
  /**
   * The branch ball this position belongs to (catchball_balls.id). Null for
   * legacy single-ball loops. A loop with parallel sibling balls has one active
   * position per branch (see catchball_active_branch_positions, migration 609).
   */
  ballId?: string | null;
}

/**
 * All turns and involvements for a loop, with the active position resolved.
 */
export interface CatchballLoopLevelState {
  loopId: string;
  turns: CatchballTierTurn[];
  involvements: CatchballTierInvolvement[];
  /**
   * The primary active position (first branch) — back-compat for single-ball
   * consumers. Equals activePositions[0] ?? null.
   */
  activePosition: ActiveBallPosition | null;
  /**
   * One active position per parallel branch (from catchball_active_branch_positions).
   * A single-ball loop has 0 or 1 entries; a fanned-out loop has one per sibling.
   */
  activePositions: ActiveBallPosition[];
}

// ============================================================================
// Action Param Types (for store)
// ============================================================================

export interface PassBallParams {
  loopId: string;
  fromOrgUnitId: string;
  toOrgUnitId: string;
  passDirection: 'down' | 'up';
  content?: Record<string, unknown>;
  /** Judgment prerequisites for the new turn. Empty = immediately passable. */
  judgmentPrerequisites?: JudgmentPrerequisite[];
  actorId: string;
  /** Cycle ID — the cycle's loops are refreshed after the pass (the holder is cleared). */
  cycleId?: string;
}

export interface InvolveLevelParams {
  loopId: string;
  holderOrgUnitId: string;
  targetOrgUnitId: string;
  involvementMode: InvolvementMode;
  requestContent?: Record<string, unknown>;
  actorId: string;
}

export interface MarkPrerequisiteSatisfiedParams {
  turnId: string;
  prereqCode: string;
  artifactRef?: { entityId: string; entityTable: string };
  actorId: string;
}

/** One recipient of a fan-out (catchball_fan_out, migration 756). */
export interface FanOutTargetParams {
  recipientOrgUnitId: string;
  recipientOrgUnitName?: string;
  /** Request payload that travels down to this child (e.g. { request_text, ... }). */
  requestPayload?: Record<string, unknown>;
}

/**
 * Split a Ball into parallel child Balls — ALL of them or none
 * (catchball_fan_out, migration 756). The actor is the signed-in person.
 */
export interface FanOutParams {
  loopId: string;
  parentBallId: string;
  targets: FanOutTargetParams[];
  /** Judgment prerequisites for each child's opening turn. Empty = immediately passable. */
  judgmentPrerequisites?: JudgmentPrerequisite[];
}

/** A target the server refused before anything was written. */
export interface FanOutInvalidTarget {
  index: number;
  target: string;
  reason: string;
}

/** Create the root ball + initiate turn for a loop (migration 610). Idempotent. */
export interface OriginateLoopParams {
  loopId: string;
  originatorOrgUnitId: string;
  originatorOrgUnitName?: string;
  requestPayload?: Record<string, unknown>;
  actorId: string;
}

export interface DeliverInvolvementParams {
  involvementId: string;
  outputContent: Record<string, unknown>;
  actorId: string;
}

export interface InitiateTurnParams {
  loopId: string;
  /** Which org unit is picking up the ball to start the loop */
  orgUnitId: string;
  /** Optional judgment prerequisites for this level's turn */
  judgmentPrerequisites?: JudgmentPrerequisite[];
  /** Optional context / purpose for the loop */
  intentContent?: Record<string, unknown>;
  actorId: string;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Returns true when all required prerequisites on a turn are satisfied.
 */
export function allRequiredPrereqsMet(turn: CatchballTierTurn): boolean {
  return turn.judgmentPrerequisites
    .filter(p => p.required)
    .every(p => turn.prerequisiteSatisfaction[p.code]?.satisfied === true);
}

/**
 * Returns the unsatisfied required prerequisites for a turn.
 */
export function unsatisfiedRequiredPrereqs(turn: CatchballTierTurn): JudgmentPrerequisite[] {
  return turn.judgmentPrerequisites.filter(
    p => p.required && !turn.prerequisiteSatisfaction[p.code]?.satisfied
  );
}

/**
 * Returns a human-readable label for a ball arrival direction.
 */
export const BALL_DIRECTION_LABELS: Record<BallArrivalDirection, string> = {
  initiate: 'Initiated by this level',
  down:     'Request received from above',
  up:       'Proposal received from below',
};

/**
 * Returns a human-readable label for a level turn state.
 */
export const LEVEL_TURN_STATE_LABELS: Record<TierTurnState, string> = {
  active:        'Working',
  ready_to_pass: 'Ready to pass',
  passed:        'Passed',
  returned:      'Returned',
};

/**
 * Returns a human-readable label for an involvement mode.
 */
export const INVOLVEMENT_MODE_LABELS: Record<InvolvementMode, string> = {
  session:  'Participate in sessions',
  delegate: 'Deliver a specific output',
};
